import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, IndianRupee, Truck, ArrowUp, MapPin, Phone, Mail, Send } from 'lucide-react';
import { FaInstagram, FaLinkedin, FaYoutube, FaFacebook } from 'react-icons/fa';
import { submitToWeb3Forms } from '../../utils/formSubmit';

const Footer = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const result = await submitToWeb3Forms(e, "Newsletter Subscription");
    setIsSubmitting(false);
    if (result.success) {
      alert("Thanks for subscribing to our newsletter!");
      e.target.reset();
    } else {
      alert(result.message);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const marqueeItems = [
    { text: "Eco Friendly", icon: <Leaf size={24} /> },
    { text: "Lower Running Costs", icon: <IndianRupee size={24} /> },
    { text: "Sustainable Transportation", icon: <Truck size={24} /> }
  ];

  // We duplicate the items a few times to ensure a smooth infinite scroll
  const repeatedMarquee = [...marqueeItems, ...marqueeItems, ...marqueeItems, ...marqueeItems, ...marqueeItems];

  const brandNavy = "text-[#3869B3]";
  const brandGreen = "text-[#6FBF4C]";
  const bgNavy = "bg-[#3869B3]";
  const textHover = "hover:text-[#6FBF4C]";

  return (
    <footer className="w-full relative font-['TT_Firs_Neue_Trl',_'Plus_Jakarta_Sans',_sans-serif]">
      {/* Infinite Marquee Banner */}
      <div className={`${bgNavy} text-white py-4 marquee-container border-b-4 border-[#6FBF4C]`}>
        <div className="marquee-content">
          {repeatedMarquee.map((item, idx) => (
            <div key={idx} className="flex items-center space-x-3 px-12">
              <span>{item.icon}</span>
              <span className="text-2xl font-bold tracking-wide uppercase">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="bg-[#E6EEFC] pt-16 pb-8 relative z-10">
        {/* Faded Watermark */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden flex justify-center pointer-events-none opacity-[0.03]">
          <span className="text-[12vw] font-bold leading-none text-[#3869B3] whitespace-nowrap">GREEN DRIVE</span>
        </div>

        <div className="w-full max-w-[1800px] mx-auto px-[20px] lg:px-[62px] relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[4fr_2fr_2fr_2fr_3fr_4fr_4fr] gap-10 lg:gap-6 mb-16">
            
            {/* Column 1: Branding & Social */}
            <div className="flex flex-col space-y-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png" alt="Green Drive Mobility Logo" className="h-24 xl:h-32 w-auto" />
              </Link>
              <h3 className={`text-xl font-bold ${brandNavy}`}>Green Drive Auto Services</h3>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/greendrivemobility" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-full border border-[#3869B3] flex items-center justify-center ${brandNavy} hover:bg-[#3869B3] hover:text-white transition-colors`}>
                  <FaLinkedin size={18} />
                </a>
                <a href="https://www.instagram.com/greendrivemobility?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-full border border-[#3869B3] flex items-center justify-center ${brandNavy} hover:bg-[#3869B3] hover:text-white transition-colors`}>
                  <FaInstagram size={18} />
                </a>
                <a href="https://www.youtube.com/@GreenDriveMobility" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-full border border-[#3869B3] flex items-center justify-center ${brandNavy} hover:bg-[#3869B3] hover:text-white transition-colors`}>
                  <FaYoutube size={18} />
                </a>
                <a href="https://www.facebook.com/GreenDriveMobility/" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-full border border-[#3869B3] flex items-center justify-center ${brandNavy} hover:bg-[#3869B3] hover:text-white transition-colors`}>
                  <FaFacebook size={18} />
                </a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>Quick Links</h4>
              <ul className="space-y-4">
                {[
                  { name: 'Home', path: '/' },
                  { name: 'About Us', path: '/about' },
                  { name: 'Investor Relations', path: '/investors' },
                  { name: 'Sitemap', path: '/sitemap' },
                  { name: 'News & Media', path: '/media' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className={`${brandNavy} ${textHover} font-medium transition-colors`}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Services */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>
                <Link to="/services" className="hover:text-[#6FBF4C] transition-colors">Services</Link>
              </h4>
              <ul className="space-y-4">
                {[
                  { name: 'EV Bike Rentals', path: '/services' },
                  { name: 'Cab & Auto Booking', path: '/services' },
                  { name: 'B2B Enterprise', path: '/services' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className={`${brandNavy} ${textHover} font-medium transition-colors`}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Industries */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>Industries</h4>
              <ul className="space-y-4">
                {[
                  { name: 'E-Commerce', path: '/industries' },
                  { name: 'FMCG', path: '/industries' },
                  { name: 'Logistics', path: '/industries' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className={`${brandNavy} ${textHover} font-medium transition-colors`}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Partner With Us */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>Partner With Us</h4>
              <ul className="space-y-4">
                {[
                  { name: 'Careers', path: '/careers' },
                  { name: 'Business Partnership', path: '/partner/business-partnership' },
                  { name: 'Become a Delivery Partner', path: '/partner/become-a-delivery-partner' }
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className={`${brandNavy} ${textHover} font-medium transition-colors`}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 5: Contact Info */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>Get in Touch</h4>
              <div className={`space-y-4 ${brandNavy} font-medium`}>
                <p className="font-bold">GREEN DRIVE MOBILITY PVT LTD</p>
                <a href="tel:+917314600311" className={`flex items-center space-x-3 ${textHover} transition-colors`}>
                  <Phone size={18} className="flex-shrink-0" />
                  <span>+91 73146 00311</span>
                </a>
                <a href="mailto:hello@greendrivemobility.com" className={`flex items-center space-x-3 ${textHover} transition-colors`}>
                  <Mail size={18} className="flex-shrink-0" />
                  <span>hello@greendrivemobility.com</span>
                </a>
                <div className="flex space-x-3 items-start mt-2">
                  <MapPin size={18} className="flex-shrink-0 mt-1" />
                  <p className="leading-relaxed">
                    No.52/2, LBT Complex<br />
                    Shop No-2, Huskur Gate<br />
                    Next to Shastra Hospital<br />
                    Bangalore - 560100
                  </p>
                </div>
              </div>
            </div>

            {/* Column 6: Newsletter Subscription */}
            <div>
              <h4 className={`text-lg font-bold mb-6 ${brandNavy}`}>Newsletter</h4>
              <p className={`text-sm font-medium mb-4 ${brandNavy}`}>Subscribe to get the latest updates on green mobility.</p>
              <form className="flex flex-col gap-3" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your email address" 
                  className={`w-full p-3 rounded-lg border border-[#3869B3]/20 bg-white/80 outline-none focus:border-[#6FBF4C] text-[#3869B3] placeholder-[#3869B3]/50 text-sm`}
                  required
                />
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`mt-[4px] w-full flex items-center justify-center bg-[#3869B3] text-white px-[20px] py-[12px] text-[16px] font-[700] rounded-[8px] shadow-[4px_4px_0px_#6FBF4C] transition-all duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed shadow-[2px_2px_0px_#6FBF4C] translate-x-[2px] translate-y-[2px]' : 'hover:shadow-[2px_2px_0px_#6FBF4C] hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px]'}`}
                >
                  <Send size={18} className="mr-2" />
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            </div>

          </div>

          {/* Other Branches */}
          <div className="mt-12 mb-8 flex flex-col gap-4 overflow-hidden w-full">
            <h4 className={`font-bold text-lg text-left ${brandNavy}`}>Other Branches:</h4>
            
            <div 
              className="marquee-container w-full overflow-hidden py-2" 
              style={{ 
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)', 
                maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' 
              }}
            >
              <div className="marquee-content flex gap-4 items-center">
                {[...Array(4)].flatMap((_, arrayIndex) => 
                  ['Pune', 'Mumbai', 'Delhi', 'Hyderabad', 'Bangalore', 'Chennai'].map((branch, index) => (
                    <div 
                      key={`${arrayIndex}-${index}`}
                      className={`px-6 py-2 rounded-full border border-[#3869B3]/20 bg-white/50 shadow-sm ${brandNavy} font-medium whitespace-nowrap hover:bg-[#3869B3]/10 transition-colors cursor-default`}
                    >
                      {branch}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`border-t border-[#3869B3]/20 pt-8 flex flex-col xl:flex-row justify-between items-center gap-6 text-sm font-medium ${brandNavy}`}>
            
            {/* Left side: Copyright & Links */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 text-center md:text-left">
              <p>© Copyright {new Date().getFullYear()} – All Rights Reserved</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/terms-conditions" className={`${textHover} transition-colors`}>Terms & Conditions</Link>
                <Link to="/refund-cancellation-policy" className={`${textHover} transition-colors`}>Refund Policy</Link>
                <Link to="/privacy-policy" className={`${textHover} transition-colors`}>Privacy Policy</Link>
              </div>
            </div>

            {/* Right side: Compliance Numbers */}
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-xs md:text-sm">
              <p><strong className="opacity-70">DPIIT:</strong> DIPPXXXXX</p>
              <p><strong className="opacity-70">CIN:</strong> UXXXXXKA2024PTCXXXXXX</p>
              <p><strong className="opacity-70">GST:</strong> 29XXXXXXXXXXXZX</p>
              
              <button 
                onClick={scrollToTop}
                className={`ml-2 w-10 h-10 rounded-full border border-[#3869B3] flex items-center justify-center ${brandNavy} hover:bg-[#3869B3] hover:text-white transition-all hover:-translate-y-1 focus:outline-none shrink-0`}
                aria-label="Back to top"
              >
                <ArrowUp size={20} />
              </button>
            </div>
            
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
